define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/_base/window",		// dojo.global
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"dijit/_WidgetsInTemplateMixin",
	"dijit/registry",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/types",
	"./FusionChartsLoader",
	"./FusionChartStore",
	"./FusionChartUtil",
	"dojo/text!csi/widget/FusionChart.html",
	"dijit/Toolbar"
], function(declare, lang, win, _TemplatedMixin, _WidgetBase, _WidgetsInTemplateMixin, registry, csi, Deferred, _, html, locale, types, FusionCharts, FusionChartStore, FusionChartUtil, template) {

	dojo.global.csiLinkFC = function(/*String*/v) {
		// summary: call FusionChart with parameter
		var a = v.split("___");
		registry.byId(a[0])._onClick(a[1]);
	};

	const _Placeholder = declare([_WidgetBase, _TemplatedMixin], {
		chartTemplate: "",
		height: 0,
		placeholderTitle: "",	// avoid using "title" property or will get unwanted tooltip
		subtitle: "",
		templateString: `<div class="csi-flex-column csi-flex-center"
			><div class="csi-fusion-chart-placeholder"></div
			><div class="csi-placeholder-title">\${placeholderTitle}</div
			><div class="csi-placeholder-description">\${subtitle}</div
		></div>`,

		startup() {
			const {chartTemplate, domNode, height} = this;
			html.setCSSVariable(domNode, "FusionChartPlaceholder", `var(--Chart${chartTemplate}GreyURL)`);
			html.setPixels(domNode, html.HEIGHT, height);
			this.inherited(arguments);
		}
	});

	/**
	 * parse value and cast to 0 if not parsed properly
	 * @param {number | string} value 
	 * @returns {number}
	 */
	function parseValueToNumber(value) {
		// first parse value to avoid power of ten representation
		const parsedValue = parseValue(value);
		
		let parsed = parseInt(parsedValue);
		if (isNaN(parsed)) {
			parsed = 0;
		}
		return parsed;
	}

	/**
	 * calculate sum of values (to be used for percentage calculations)
	 * @param {number[]} values 
	 * return {number}
	 */
	function calcSum(values) {
		let sum = 0;
		values.forEach((val) => {
			sum += parseValueToNumber(val);
		});
		return sum;
	}

	/**
	 * calculate sum of 2 dimensional array of values (to be used for percentage calculations)
	 * @param {number[][]} values 
	 * return {number}
	 */
	function calcTotalSum(values) {
		let sum = 0;
		values.forEach((val) => {
			sum = sum + calcSum(val);
		});
		return sum;
	}

	/**
	 * calculate sum of values in a particular column (to be used for percentage calculations)
	 * @param {number[][]} values 
	 * @param {number} i - column to be summed
	 * return {number}
	 */
	function calcColSum(values, i) {
		var sum = 0;
		var y = 0;
		while (y < values.length) {
			if (i < values[y].length) {
				sum = sum + parseValueToNumber(values[y][i]);
			}
			y = y + 1;
		}
		return sum;
	}

	/**
	 * find the topmost defined value in a column in a 2-dimensional array.  Used for stacked area charts.
	 * @param {number[][]} values 
	 * @param {number} i - column
	 * return {number}
	 */
	function findTopOfCol(values, i) {
		var top = 0;
		var y = 0;
		while (y < values.length) {
			if (i < values[y].length && values[y][i]) {
				top = y;
			}
			y = y + 1;
		}
		return top;
	}

	function findNumCols(values) {
		var longest = 0;
		var i = 0;
		while (i < values.length) {
			longest = Math.max(values[i].length, longest); 
			i = i + 1;
		}

		return longest;
	}

	/**
	 * Create categories used for charts
	 * @param {object} obj - chart data object
	 * @param {string[]} values - labels for categores
	 */
	function createCategories(obj, values) {
		obj.categories = obj.categories || [{}];

		var cats = obj.categories[0];
		var tempCat = cats.category?.[0] ?? {};

		cats.category = _.map(values, function(x) {
			return lang.mixin({"label": x[1]}, tempCat);
		});
	}

	/**
	 * this function converts string to integer and/or removing trailing 0 in decimal places. Example: "3.0" => 3
	 * @param {number | string} value 
	 * @returns {number | string}
	 */
	function parseValue(value) {
		let parsedValue = Number(value);
		if (isNaN(parsedValue)) {
			parsedValue = value;
		}
		return parsedValue;
	}


	/**
	 * create string to display label, value and percentage
	 * @param {string} label - label/name of the value
	 * @param {string|number} value - value to display
	 * @param {number} sum - sum of values
	 * @param {Object} objChart - chart configuration object
	 * @param {boolean} bPercentOnly - don't show value, only show percentage
	 * @param {boolean} showLabels - whether to include the label on the chart
	 * @param {boolean} showPercents - whether to show percents
	 * @returns {string}
	 */
	function createDisplayValue(label, value, sum, objChart, bPercentOnly, showLabels, showPercents) {
		const {
			decimals,
			chartPercentageDecimalPlaces,
			numberPrefix,
			numberSuffix,
		} = objChart;
		const useLabel = showLabels ? label : undefined;
		let displayValue = useLabel ? useLabel : "";
		displayValue += (useLabel && (!bPercentOnly || showPercents)) ? ", " : "";
		if (!bPercentOnly) {
			// convert possible string to number
			let parsedValue = parseValue(value);
			// do not abbreviate if suffix is defined due to possible conflicts (suffix might be not compatible with abbreviation)
			const bNotAbbreviate = numberSuffix || parsedValue < 1000;
			// fusion charts ignores decimals setting when do abbreviation and always use 1 decimal, we will do the same here if abbreviate
			parsedValue = bNotAbbreviate ? parsedValue.toFixed(decimals) : types.abbreviateNumber(parsedValue, 1);
			// remove trailing .000
			parsedValue = parseValue(parsedValue);

			displayValue += (numberPrefix || "") + parsedValue + (numberSuffix || "");
			displayValue += showPercents ? ", " : "";
		}
		if (showPercents) {
			displayValue += parseFloat(value / sum * 100).toFixed(chartPercentageDecimalPlaces) + "%";
		}
		return displayValue;
	}



	return declare([_WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin], {
		templateString: template,

		// attach points
		chartNode: null,
		titleNode: null,
		editNode: null,
		removeNode: null,

		// Boolean: indicate if chart rendering should be animated
		bAnimate: false,

		// Boolean: indicate if rendering chart for printing
		bForPrint: false,

		// Boolean: indicate if links should be enabled
		bLink: false,

		// Object: additional parameters to mixin with chart
		params: null,

		// Any[]: array of values - if both x and y specified, then it is 2D array
		data: null,

		// Deferred: deferred object for chart that gets resolved when chart is fully rendered
		deferred: null,

		// FusionChartSettings: chart settings from dialog
		// 	{
		//		chartTemplate: String,
		//		chartName: String,
		//		chartWidth: int,
		//		charHeight: int,
		//		chartGroupX: String,
		//		chartGroupY: String,
		//		chartValue: String,
		//		chartValue2: String
		//	}
		settings: null,

		// String: number format for values
		format: null,

		// String: axis labels
		xLabel: null,
		yLabel: null,

		// String[][2]: group/stack value/name pairs
		xValues: null,
		yValues: null,

		// FusionCharts object
		fusionChart: null,

		onClick: function(/*Any*/value1, /*Any?*/value2) {
			// summary: extension point for click on chart
		},

		onEdit: function() {
			// summary: call extension point to indicate when chart is edited
		},

		onRemove: function() {
			// summary: extension point for removing chart
		},

		destroy: function() {
			// summary: destroy body-level element created by chart for "smart labels"
			if (this.fusionChart &&
				this.fusionChart.annotations &&
				this.fusionChart.annotations._renderer &&
				this.fusionChart.annotations._renderer.smartLabel &&
				this.fusionChart.annotations._renderer.smartLabel.parentContainer) {
				html.destroyElement(this.fusionChart.annotations._renderer.smartLabel.parentContainer);
			}

			_WidgetBase.prototype.destroy.call(this);
		},

		startup: function() {
			// summary: render chart
			_WidgetBase.prototype.startup.call(this);

			const {
				chartNode,
				settings,
				titleNode,
			} = this;

			// replace 3D chart with 2D variant now that we no longer support 3D charts
			let {
				chartHeight,
				chartName,
				chartShowLabels,
				chartShowPercentageValues,
				chartShowValues,
				chartSubName,
				chartTemplate,
				chartWidth,
				chartPercentageDecimalPlaces,
			} = settings;

			html.text(titleNode, chartName);

			if (chartTemplate.indexOf("3D") >= 0) {
				// don't change settings directly as we don't want the custom view to show as modified just because of this
				chartTemplate = chartTemplate.replace(/3D/, "2D");
			}

			if (chartWidth) {
				html.setPixels(this.domNode, html.WIDTH, chartWidth);
			}

			var item = FusionChartStore.get(chartTemplate);
			var m = this.data;

			if (!item || !m || !m.length) {
				// render chart placeholder
				new _Placeholder({
					chartTemplate,
					height: chartHeight,
					placeholderTitle: locale.resource("ChartPlaceholderTitle"),
					subtitle: locale.resource("ChartPlaceholderSubtitle"),
				}).placeAt(chartNode);
				return;
			}

			var obj = item.template ? lang.clone(item.template) : {};
			var templateChart = obj.chart;
			var showLabels = chartShowLabels ? "1" : "0";

			// We allow to show value and show percentage together and separately. We have following 3 variants:
			// 
			// To show only numeric values user set chartShowValues = 1 and chartShowPercentageValues = 0 
			//    and we set set showValues = "1" and showPercentValues = "0"
			// 
			// To show only percents user set chartShowValues = 0 and chartShowPercentageValues = 1 
			//    and we set set showValues = "1" and showPercentValues = "1"
			//
			// To show both values and percents user set chartShowValues = 1 and chartShowPercentageValues = 1 
			//    and we set set showValues = "1" and showPercentValues = "1" and use "displayValue" data property to create custom label
			var showValues = (chartShowValues || (!chartShowValues && chartShowPercentageValues)) ? "1" : "0";
			var showPercentageValues = chartShowPercentageValues ? "1" : "0";

			// only necessary for store to not alter object
			delete obj._type;

			// create main chart object - params override defaults, template overrides params
			obj.chart = lang.mixin({
				"animation": this.bAnimate ? 1 : 0,
				"caption": chartSubName,
				"chartPercentageDecimalPlaces": chartPercentageDecimalPlaces,
				"showLabels": showLabels,
				"showValues": showValues,
				"showPercentValues": showPercentageValues,
				"decimalSeparator": types.getNumberDecimal(),
				"thousandSeparator": types.getNumberGroup(),
				"theme": 'centrictheme',
				"xAxisName": this.xLabel || "",
				"yAxisName": this.yLabel || ""
			}, this.params || {});

			if (templateChart) {
				lang.mixin(obj.chart, templateChart);
			}

			FusionChartUtil._addNumberFormatting(obj.chart, this.format);
			FusionChartUtil._formatPercentage(m, this.format);

			if (chartTemplate == "StackedBar2D") {
				// For Stacked Bar charts, we don't display any zeroes and group by y-values.
				createCategories(obj, this.xValues);

				// create data sets
				var tempDS = obj.dataset ? obj.dataset[0] : {};
				obj.dataset = _.map(this.yValues, function(y, i) {
					var ds = lang.mixin({"seriesname": y[1]}, tempDS);
					const sum = chartShowPercentageValues ? calcSum(m[i]) : 0;

					ds.data = _.map(this.xValues, function(x, j) {
						if (sum) {
							const displayValue = m[i][j] ? createDisplayValue("", m[i][j], sum, obj.chart, !chartShowValues, chartShowLabels, true) : " ";
							return this._addLink({"value": m[i][j] || 0, displayValue, "tooltext": displayValue}, j, i);
						}
						const displayValue = m[i][j] ? undefined : " ";
						return this._addLink({"value": m[i][j] || 0, displayValue}, j, i);
					}, this);

					return ds;
				}, this);
			} else if (chartTemplate == "StackedColumn2D") {
				// For Stacked Column charts, we don't display any zeroes and group by x-values.
				createCategories(obj, this.xValues);

				// create data sets
				var tempDS = obj.dataset ? obj.dataset[0] : {};
				const colSums = [];
				const numCols = findNumCols(m);
				
				for (var i = 0; i < numCols; i++) {
					colSums.push(calcColSum(m, i));
				}
				obj.dataset = _.map(this.yValues, function(y, i) {
					var ds = lang.mixin({"seriesname": y[1]}, tempDS);
					const sum = chartShowPercentageValues ? calcSum(m[i]) : 0;

					ds.data = _.map(this.xValues, function(x, j) {
						if (sum) {
							const displayValue = m[i][j] ? createDisplayValue("", m[i][j] || 0, colSums[j], obj.chart, !chartShowValues, chartShowLabels, true) : " ";
							return this._addLink({"value": m[i][j] || 0, displayValue, "tooltext": displayValue}, j, i);
						}
						const displayValue = m[i][j] ? undefined : " ";
						return this._addLink({"value": m[i][j] || 0, displayValue}, j, i);
					}, this);

					return ds;
				}, this);
			} else if (chartTemplate == "StackedArea2D") {
				// For stacked area charts, we only display the overall values for the category
				const totalSum = chartShowPercentageValues ? calcTotalSum(m) : 0;
				createCategories(obj, this.xValues);

				// create data sets
				var tempDS = obj.dataset ? obj.dataset[0] : {};
				const colSums = [];
				const colTops = [];
				const numCols = findNumCols(m);
				for (var i = 0; i < numCols; i++) {
					colSums.push(calcColSum(m, i));
					colTops.push(findTopOfCol(m, i));
				}
				obj.dataset = _.map(this.yValues, function(y, i) {
					var ds = lang.mixin({"seriesname": y[1]}, tempDS);
					const sum = chartShowPercentageValues ? calcSum(m[i]) : 0;

					ds.data = _.map(this.xValues, function(x, j) {
						if (i == colTops[j]) {
							if (sum) {
								const displayValue = createDisplayValue("", colSums[j], totalSum, obj.chart, !chartShowValues, chartShowLabels, true);
								return this._addLink({"value": m[i][j] || 0, displayValue, "tooltext": displayValue}, j, i);
							}
							return this._addLink({"value": m[i][j] || 0, "displayValue": `${colSums[j]}`}, j, i);
						}
						return this._addLink({"value": m[i][j] || 0, "displayValue": " ", "tooltext": ""}, j, i);
					}, this);

					return ds;
				}, this);
			} else if (this.yValues) {
				createCategories(obj, this.xValues);

				// create data sets
				var tempDS = obj.dataset ? obj.dataset[0] : {};
				obj.dataset = _.map(this.yValues, function(y, i) {
					var ds = lang.mixin({"seriesname": y[1]}, tempDS);
					const sum = chartShowPercentageValues ? calcSum(m[i]) : 0;

					ds.data = _.map(this.xValues, function(x, j) {
						if (sum) {
							const displayValue = createDisplayValue("", m[i][j], sum, obj.chart, !chartShowValues, chartShowLabels, true);
							return this._addLink({"value": m[i][j] || 0, displayValue, "tooltext": displayValue}, j, i);
						}
						return this._addLink({"value": m[i][j] || 0}, j, i);
					}, this);

					return ds;
				}, this);
			} else if(chartTemplate == "Pie2D" || chartTemplate=="Doughnut2D") {
				// if chart should show percentage - calculate percentage and create custom label to show both values and percents
				const sum = chartShowPercentageValues ? calcSum(m) : 0;
				// create data
				obj.data = _.map(this.xValues, (x, j) => {
					const v = m[j] || 0;
					// for pie and doughnut charts, we need to always create a custom label as it hides display values at 13+ elements
					const displayValue = createDisplayValue(x[1], v, sum, obj.chart, !chartShowValues, chartShowLabels, sum);
					return this._addLink({"label": displayValue, "value": v, "tooltext": displayValue}, j, -1);
				}, this);
				obj.chart.showLabels = true;
				obj.chart.showValues = false;
			} else {
				// if chart should show percentage - calculate percentage and create custom label to show both values and percents
				const sum = chartShowPercentageValues ? calcSum(m) : 0;
				// create data
				obj.data = _.map(this.xValues, (x, j) => {
					const v = m[j] || 0;
					// if we were able to calculate non zero sum, then now calculate percentage
					const displayValue = createDisplayValue(x[1], v, sum, obj.chart, !chartShowValues, chartShowLabels, sum);
					if (sum) {
						return this._addLink({"label": x[1], "value": v, displayValue, "tooltext": displayValue}, j, -1);
					}
					// if sum is not available, just show data without custom label
					return this._addLink({"label": x[1], "value": v}, j, -1);
				}, this);

				// if Pareto chart, then sort by value
				if (chartTemplate.indexOf("Pareto") == 0) {
					obj.data = csi.sort(obj.data, "value");
				}
			}

			// finally render chart
			var dfd = this.deferred = new Deferred();

			try {
				this.fusionChart = new FusionCharts({
					id: this.id + "_Chart",
					type: chartTemplate.toLowerCase(),			// FusionCharts has issue with uppercase names from past, so force to lowercase
					height: chartHeight,
					width: "100%",
					dataFormat: 'json',
					dataSource: obj,
					renderAt: chartNode,

					events: {
						"rendered": function(eventObj, dataObj) {
							dfd.resolve();
						}
					}
				}).render();

				// wait for half a second at most for chart to render when not printing
				if (!this.bForPrint) {
					_.delay(function() {
						dfd.resolve();
					}, 500);
				}
			} catch (ex) {
				dfd.resolve();
				console.warn(ex);
			}
		},

		removeEditingWidgets: function() {
			this.removeNode.destroyRecursive();
			this.removeNode = null;

			this.editNode.destroyRecursive();
			this.editNode = null;
		},

		_addLink: function(/*Object*/data, /*Number*/nx, /*Number*/ny) {
			// summary: return link for chart in form "j-function-parameter"
			if (this.bLink) {
				data.link = "j-csiLinkFC-" + this.id + "___" + nx + "^^^" + ny;
			}

			return data;
		},

		_onClick: function(/*String*/v) {
			// summary: call extension point
			var a = v.split("^^^");
			var nx = Number(a[0]);
			var ny = Number(a[1]);
			this.onClick(this.xValues[nx][0], (ny >= 0) ? this.yValues[ny][0] : null);
		},

		_onRemove: function() {
			// summary: destroy chart element
			this.onRemove();
		},

		_onEdit: function() {
			// summary: edit the chart
			this.onEdit();
		}
	});
});